import { useEffect, useState } from "react";
import { Loader2, Search, Eye, Package, User, MapPin, Truck, Download, Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatPrice, formatDate } from "@/lib/format";
import { useLanguage } from "@/context/LanguageContext";
import api, { apiError } from "@/lib/api";

const STATUSES = [
  { value: "pending", es: "Pendiente", en: "Pending" },
  { value: "processing", es: "En proceso", en: "Processing" },
  { value: "shipped", es: "Enviado", en: "Shipped" },
  { value: "delivered", es: "Entregado", en: "Delivered" },
  { value: "cancelled", es: "Cancelado", en: "Cancelled" },
];
const STATUS_COLOR = {
  pending: "text-amber-500", processing: "text-blue-500", shipped: "text-indigo-500",
  delivered: "text-emerald-500", cancelled: "text-red-500",
};
const APPROVAL_TABS = [
  { value: "pending", es: "Pendientes", en: "Pending" },
  { value: "approved", es: "Aprobadas", en: "Approved" },
  { value: "rejected", es: "Rechazadas", en: "Rejected" },
];

export default function AdminRequests() {
  const { lang } = useLanguage();
  const es = lang === "es";
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("pending");
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tracking, setTracking] = useState("");

  useEffect(() => { setTracking(active?.tracking_number || ""); }, [active]);

  const load = () => {
    setLoading(true);
    api.get("/admin/requests").then((r) => setRequests(r.data.requests)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const statusLabel = (v) => (STATUSES.find((s) => s.value === v) || {})[lang] || v;
  const approvalOf = (o) => o.approval_status || "pending";

  const updateStatus = async (order, status, tracking) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/orders/${order.id}`, { status, tracking_number: tracking });
      toast.success(es ? "Solicitud actualizada" : "Request updated");
      setActive(data.order);
      setRequests((prev) => prev.map((o) => (o.id === order.id ? data.order : o)));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const updateApproval = async (order, approval_status) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/orders/${order.id}`, { approval_status });
      toast.success(approval_status === "approved"
        ? (es ? "Solicitud aprobada" : "Request approved")
        : (es ? "Solicitud rechazada" : "Request rejected"));
      setActive(data.order);
      setRequests((prev) => prev.map((o) => (o.id === order.id ? data.order : o)));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const counts = APPROVAL_TABS.reduce((acc, t) => {
    acc[t.value] = requests.filter((o) => approvalOf(o) === t.value).length;
    return acc;
  }, {});

  const filtered = requests.filter((o) => {
    if (approvalOf(o) !== tab) return false;
    const q = search.toLowerCase();
    return !q || o.order_number?.toLowerCase().includes(q) ||
      (o.customer?.name || "").toLowerCase().includes(q) ||
      (o.customer?.email || "").toLowerCase().includes(q);
  });

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const headers = ["Solicitud", "Fecha", "Nombre", "Correo", "Telefono", "Estado", "Envio", "Valor", "Direccion", "Piezas"];
    const rows = filtered.map((o) => {
      const a = o.shipping_address || {};
      const addr = [a.full_name, a.line1, a.line2, a.city, a.state, a.postal_code, a.country].filter(Boolean).join(", ");
      const items = (o.items || []).map((i) => `${i.name} x${i.quantity}`).join(" | ");
      return [
        o.order_number, formatDate(o.created_at), o.customer?.name, o.customer?.email, o.customer?.phone,
        statusLabel(o.status), o.shipping_method === "express" ? "Expres" : "Normal", o.subtotal, addr, items,
      ].map(esc).join(",");
    });
    const csv = "\uFEFF" + [headers.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `solicitudes-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-requests">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-4xl font-light">{es ? "Solicitudes" : "Requests"}</h1>
          <p className="text-muted-foreground text-sm mt-1">{requests.length} {es ? "solicitudes recibidas" : "requests received"}</p>
        </div>
        <Button variant="outline" className="rounded-none" onClick={exportCsv} disabled={!filtered.length} data-testid="export-requests-btn">
          <Download className="h-4 w-4 mr-2" /> {es ? "Exportar CSV" : "Export CSV"}
        </Button>
      </div>

      <div className="flex gap-1 border-b border-border mb-6">
        {APPROVAL_TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            data-testid={`requests-tab-${t.value}`}
            className={`px-5 py-3 text-sm border-b-2 -mb-px transition-colors ${tab === t.value ? "border-gold text-gold" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            {t[lang]}
            <span className="ml-2 text-xs px-1.5 py-0.5 bg-secondary rounded-full" data-testid={`requests-count-${t.value}`}>{counts[t.value]}</span>
          </button>
        ))}
      </div>

      <div className="relative mb-5 max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={es ? "Buscar por # / nombre / correo" : "Search by # / name / email"} className="rounded-none pl-9" data-testid="request-search" />
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        {loading ? (
          <div className="py-24 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground" data-testid="requests-empty">
            {tab === "pending" ? (es ? "No hay solicitudes pendientes." : "No pending requests.")
              : tab === "approved" ? (es ? "No hay solicitudes aprobadas." : "No approved requests.")
                : (es ? "No hay solicitudes rechazadas." : "No rejected requests.")}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="p-4 font-medium overline">{es ? "Solicitud" : "Request"}</th>
                <th className="p-4 font-medium overline hidden md:table-cell">{es ? "Cliente" : "Client"}</th>
                <th className="p-4 font-medium overline hidden lg:table-cell">{es ? "Fecha" : "Date"}</th>
                <th className="p-4 font-medium overline">{es ? "Valor" : "Value"}</th>
                <th className="p-4 font-medium overline">{es ? "Estado" : "Status"}</th>
                <th className="p-4 font-medium overline text-right">{es ? "Detalle" : "Detail"}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr key={o.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`request-row-${o.id}`}>
                  <td className="p-4 font-medium">{o.order_number}</td>
                  <td className="p-4 hidden md:table-cell">
                    <p>{o.customer?.name}</p>
                    <p className="text-xs text-muted-foreground">{o.customer?.email}</p>
                  </td>
                  <td className="p-4 hidden lg:table-cell text-muted-foreground">{formatDate(o.created_at)}</td>
                  <td className="p-4">{formatPrice(o.subtotal)}</td>
                  <td className="p-4"><span className={`capitalize ${STATUS_COLOR[o.status] || ""}`}>{statusLabel(o.status)}</span></td>
                  <td className="p-4 text-right">
                    <Button variant="ghost" size="icon" onClick={() => setActive(o)} data-testid={`view-request-${o.id}`}><Eye className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="request-detail-dialog">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">{es ? "Solicitud" : "Request"} {active.order_number}</DialogTitle>
                <DialogDescription>{formatDate(active.created_at)}</DialogDescription>
              </DialogHeader>

              <div className="flex flex-wrap items-center gap-3 border border-border bg-secondary/30 p-3" data-testid="approval-bar">
                <span className="overline text-muted-foreground">{es ? "Estado de aprobación" : "Approval status"}:</span>
                <span
                  className={`text-sm font-medium capitalize ${approvalOf(active) === "approved" ? "text-emerald-500" : approvalOf(active) === "rejected" ? "text-red-500" : "text-amber-500"}`}
                  data-testid="approval-state"
                >
                  {approvalOf(active) === "approved" ? (es ? "Aprobada" : "Approved")
                    : approvalOf(active) === "rejected" ? (es ? "Rechazada" : "Rejected")
                      : (es ? "Pendiente" : "Pending")}
                </span>
                <div className="flex gap-2 ml-auto">
                  {approvalOf(active) !== "approved" && (
                    <Button size="sm" className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white" disabled={saving} onClick={() => updateApproval(active, "approved")} data-testid="approve-request-btn">
                      <Check className="h-4 w-4 mr-1" /> {es ? "Aprobar" : "Approve"}
                    </Button>
                  )}
                  {approvalOf(active) !== "rejected" && (
                    <Button size="sm" variant="outline" className="rounded-none" disabled={saving} onClick={() => updateApproval(active, "rejected")} data-testid="reject-request-btn">
                      <X className="h-4 w-4 mr-1" /> {es ? "Rechazar" : "Reject"}
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6 mt-2">
                <div className="space-y-1.5">
                  <p className="overline text-gold flex items-center gap-2"><User className="h-3.5 w-3.5" /> {es ? "Contacto" : "Contact"}</p>
                  <p className="text-sm font-medium">{active.customer?.name}</p>
                  <p className="text-sm text-muted-foreground" data-testid="req-detail-email">{active.customer?.email}</p>
                  <p className="text-sm text-muted-foreground" data-testid="req-detail-phone">{active.customer?.phone}</p>
                </div>
                <div className="space-y-1.5">
                  <p className="overline text-gold flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {es ? "Envío" : "Shipping"}</p>
                  <p className="text-sm">{active.shipping_address?.full_name}</p>
                  <p className="text-sm text-muted-foreground">{active.shipping_address?.line1}{active.shipping_address?.line2 ? `, ${active.shipping_address.line2}` : ""}</p>
                  <p className="text-sm text-muted-foreground">{active.shipping_address?.city}, {active.shipping_address?.state} {active.shipping_address?.postal_code}</p>
                  <p className="text-sm text-muted-foreground">{active.shipping_address?.country}</p>
                  <p className="text-sm flex items-center gap-1.5 mt-1"><Truck className="h-3.5 w-3.5 text-gold" /> {active.shipping_method === "express" ? (es ? "Envío exprés" : "Express shipping") : (es ? "Envío normal" : "Standard shipping")}</p>
                </div>
              </div>

              <div className="mt-2">
                <p className="overline text-gold flex items-center gap-2 mb-3"><Package className="h-3.5 w-3.5" /> {es ? "Piezas solicitadas" : "Requested pieces"}</p>
                <div className="space-y-3 border border-border p-4">
                  {active.items?.map((i, idx) => (
                    <div key={idx} className="flex gap-3 items-center" data-testid={`req-item-${idx}`}>
                      <div className="w-12 h-14 bg-secondary overflow-hidden shrink-0"><img src={i.image} alt={i.name} className="h-full w-full object-cover" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{i.name}</p>
                        <p className="text-xs text-muted-foreground">{es ? "Cantidad" : "Qty"} {i.quantity}{i.size ? ` · ${i.size}` : ""}{i.color ? ` · ${i.color}` : ""}</p>
                      </div>
                      <span className="text-sm">{formatPrice(i.price * i.quantity)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between border-t border-border pt-3 font-serif text-lg">
                    <span>{es ? "Valor estimado" : "Estimated value"}</span>
                    <span>{formatPrice(active.subtotal)}</span>
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 border-t border-border pt-4">
                <div>
                  <Label className="overline text-muted-foreground">{es ? "Estado" : "Status"}</Label>
                  <Select value={active.status} onValueChange={(v) => updateStatus(active, v, active.tracking_number)}>
                    <SelectTrigger className="rounded-none mt-1.5" data-testid="req-status-select" disabled={saving}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s[lang]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="overline text-muted-foreground">{es ? "N.º de seguimiento" : "Tracking number"}</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input value={tracking} onChange={(e) => setTracking(e.target.value)} className="rounded-none" data-testid="req-tracking-input" />
                    <Button variant="outline" className="rounded-none" disabled={saving} onClick={() => updateStatus(active, active.status, tracking)} data-testid="req-tracking-save">{es ? "Guardar" : "Save"}</Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Label({ className = "", children }) {
  return <p className={className}>{children}</p>;
}
